import { cbSemanticCssVars, Typography as T } from '@creditornot/cb-ingredients';
import { Empty } from '@creditornot/cb-placeholders';
import React, { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import styled from 'styled-components';

import { WideAreaCheckbox } from 'common/components';
import { isDefined } from 'common/utils';

import VenueListRowRenderer, { VenueRowData } from './VenueListRowRenderer';
import { NAME_COLUMN_WIDTH } from './constants';
import type { VenueResult } from './types';

type Props = {
  onRowClick: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  selectedMenuIds: string[];
  venues: VenueResult[];
};

const ROW_HEIGHT = 56;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
  height: 100vh;
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  height: ${ROW_HEIGHT}px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const Wrapper = styled.div`
  flex: 1 1 auto;
`;

const TableHeaderCell = styled(T.Subtitle)`
  font-weight: bold;
  align-items: center;
  display: flex;
  padding: 0 24px;
  text-align: start;
`;

const NameHeader = styled(TableHeaderCell)`
  width: ${NAME_COLUMN_WIDTH}vw;
`;

const AddressHeader = styled(TableHeaderCell)``;

const EmptyMessage = styled(Empty)`
  margin: 48px 16px;
`;

export const getVenueSelectionStatus = (venues: VenueResult[], selectedMenuIds: string[]) =>
  [
    !venues.length || !selectedMenuIds?.length ? ('not-selected' as const) : undefined,
    venues.every(venue => venue.menu_id && selectedMenuIds?.includes(venue.menu_id))
      ? ('selected' as const)
      : undefined,
    venues.some(venue => venue.menu_id && selectedMenuIds?.includes(venue.menu_id))
      ? ('partially-selected' as const)
      : undefined,
    'not-selected' as const,
  ].filter(isDefined)[0];

export const MenuCopyVenueList = ({ onRowClick, onDeselectAll, onSelectAll, selectedMenuIds, venues }: Props) => {
  const intl = useIntl();
  const listRef = useRef<FixedSizeList>(null);

  return (
    <Root>
      <Header>
        <WideAreaCheckbox
          onToggle={
            venues.every(venue => venue.menu_id && selectedMenuIds.includes(venue.menu_id))
              ? onDeselectAll
              : onSelectAll
          }
          selectionStatus={getVenueSelectionStatus(venues, selectedMenuIds)}
        />
        <NameHeader>
          <FormattedMessage id="menu.menu-sync.venue-name" />
        </NameHeader>
        <AddressHeader>
          <FormattedMessage id="menu.menu-sync.venue-address" />
        </AddressHeader>
      </Header>
      <Wrapper>
        {venues.length === 0 && <EmptyMessage title={intl.formatMessage({ id: 'menu.menu-sync.empty-venue-list' })} />}
        {venues.length > 0 && (
          <AutoSizer disableWidth>
            {({ height }) => (
              <FixedSizeList<VenueRowData>
                height={height}
                width="100%"
                itemData={{ onRowClick, selectedMenuIds, venues }}
                itemCount={venues.length}
                itemSize={ROW_HEIGHT}
                itemKey={index => venues[index].venue_id}
                ref={listRef}
              >
                {VenueListRowRenderer}
              </FixedSizeList>
            )}
          </AutoSizer>
        )}
      </Wrapper>
    </Root>
  );
};
